import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, Pagination } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import GradientCircularProgress from '../../../components/common/GradientCircularProgress';
import useBackendApi from '../../../hooks/useBackendApi';
import { RequestOptions } from '../../../types/httpRequests';
import { useThemeContext } from '../../../contexts/ThemeContext.js';
import TableCard, { TableColumn } from '../../../components/common/TableCard';
import { formatDateTime } from '../../../utils/dateTime';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

interface Order {
  order_id?: string | null;
  s_order_name: string;
  order_date: string;
  customer_name: string;
  fulfillment_status: string;
  total_quantity: number;
  total_amount: string;
}

interface OrdersResponse {
  message: string;
  orders: Order[];
  total_count: number;
}

export default function OrdersListOrg(): JSX.Element {
  const { theme } = useThemeContext();
  const { org_path_name } = useParams<{ org_path_name: string }>();
  const navigate = useNavigate();
  const { apiFetchRequest, isLoading } = useBackendApi();

  const [orders, setOrders] = useState<Order[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [errorText, setErrorText] = useState<string | null>(null);

  const fetchOrders = useCallback(
    async (currentPage: number) => {
      if (!org_path_name) return;

      const opts: RequestOptions = {
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/orders?page=${currentPage}`,
        method: 'GET',
      };

      try {
        const res = await apiFetchRequest(opts);
        if (!res) {
          setErrorText('Unable to load orders. Please try again.');
          return;
        }

        const { status, body } = res;
        const unauthorized =
          status === 401 ||
          status === 403 ||
          body?.status_code === 401 ||
          body?.status_code === 403 ||
          body?.message === 'UNAUTHENTICATED' ||
          body?.message === 'UNAUTHORIZED' ||
          body?.message === 'NO_ACCESS';

        if (unauthorized) {
          navigate('/401.html');
          return;
        }

        if (status !== 200) {
          setErrorText('Unable to load orders. Please try again.');
          return;
        }

        if (body?.message === 'SUCCESS') {
          setOrders(body.orders || []);
          setTotalCount(body.total_count || 0);
          setErrorText(null);
        } else {
          setErrorText('Unable to load orders. Please try again.');
        }
      } catch (err) {
        setErrorText('Something went wrong. Please try again.');
      }
    },
    [apiFetchRequest, navigate, org_path_name],
  );

  useEffect(() => {
    fetchOrders(page);
  }, [fetchOrders, page]);

  const columns = useMemo<Array<TableColumn<Order>>>(
    () => [
      {
        label: 'Order ID',
        render: (order) => order.s_order_name || '-',
      },
      {
        label: 'Date',
        render: (order) => formatDateTime(order.order_date, { fallback: 'N/A' }),
      },
      {
        label: 'Customer',
        render: (order) => order.customer_name || '-',
      },
      {
        label: 'Fulfillment Status',
        render: (order) => (
          <Box
            sx={{
              textTransform: 'capitalize',
              backgroundColor: order.fulfillment_status === 'fulfilled' ? '#e3f2fd' : '#f5f5f5',
              px: 1,
              borderRadius: 1,
              display: 'inline-block',
            }}
          >
            {order.fulfillment_status || 'unfulfilled'}
          </Box>
        ),
      },
      {
        label: 'Items',
        render: (order) => order.total_quantity || 0,
      },
      {
        label: 'Total',
        render: (order) => order.total_amount || '-',
      },
    ],
    [],
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleOrderRowClick = useCallback(
    (order: Order) => {
      const targetOrderId = order.order_id ?? order.s_order_name;
      if (!org_path_name || !targetOrderId) {
        return;
      }

      navigate(`/o/${encodeURIComponent(org_path_name)}/orders/${encodeURIComponent(targetOrderId)}`);
    },
    [navigate, org_path_name],
  );

  if (isLoading && orders.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: theme.layout.defaults.backgroundColor, minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: theme.custom.components.headerBanner.title.color }}>
          Orders
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate(`/o/${org_path_name}`)}
          sx={{ borderColor: theme.buttons.ghost.color, color: theme.buttons.ghost.color }}
        >
          Back to Organization
        </Button>
      </Box>

      {errorText ? (
        <Typography color="error">{errorText}</Typography>
      ) : (
        <>
          <TableCard
            rows={orders}
            columns={columns}
            getRowKey={(order) => order.order_id ?? order.s_order_name}
            onRowClick={handleOrderRowClick}
          />
          {totalCount > 10 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination count={Math.ceil(totalCount / 10)} page={page} onChange={handlePageChange} color="primary" />
            </Box>
          )}
          {orders.length === 0 && !isLoading && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography>No orders found.</Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
